import * as React from 'react';

import { AgentType } from '@rematter/frontend-lib-api-client';
import { Button, SearchField, Select, Spacer, Menu } from '@rematter/ui-web';
import { useAtom } from 'jotai';
import { LucideChevronDown, LucidePlusCircle } from 'lucide-react';

import * as state from '../../_state/agentList.state';
import { useTranslations } from '../../i18n/useTranslations';
import { adminRoutes } from '../../routes';

import { useDebouncedAtom } from '~/shared/hooks/useDebouncedAtom';
import { useModuleLocation } from '~/shared/hooks/useModuleLocation';

export const AgentFilterBar = () => {
  const t = useTranslations();
  const [, navigate] = useModuleLocation();
  const [searchTerm, setSearchTerm] = useDebouncedAtom(state.$searchTerm, 300);
  const [typeFilter, setTypeFilter] = useAtom(state.$typeFilter);
  const [activeFilter, setActiveFilter] = useAtom(state.$activeFilter);

  const hasFilters =
    searchTerm || typeFilter !== 'all' || activeFilter !== 'all';

  const clearFilters = () => {
    setSearchTerm('');
    setTypeFilter('all');
    setActiveFilter('all');
  };

  return (
    <>
      <SearchField
        autoFocus
        aria-label={t.aiManagement.agents.searchPlaceholder}
        placeholder={t.aiManagement.agents.searchPlaceholder}
        className="w-[250px]"
        value={searchTerm}
        onChange={(value) => setSearchTerm(value)}
      />

      <Select<'all' | AgentType>
        aria-label={t.aiManagement.agents.filterByType}
        placeholder={t.aiManagement.agents.filterByType}
        className="w-[200px]"
        value={typeFilter}
        onChange={(key) => setTypeFilter(key as 'all' | AgentType)}
        options={[
          { key: 'all', label: t.aiManagement.agents.tabs.all },
          { key: 'Agent', label: t.aiManagement.agents.type.agent },
          { key: 'SubAgent', label: t.aiManagement.agents.type.subAgent },
          {
            key: 'FileProcessor',
            label: t.aiManagement.agents.type.fileProcessor,
          },
        ]}
      />

      <Select<'all' | 'active' | 'inactive'>
        aria-label={t.aiManagement.agents.filterByStatus}
        placeholder={t.aiManagement.agents.filterByStatus}
        className="w-[200px]"
        value={activeFilter}
        onChange={(key) =>
          setActiveFilter(key as 'all' | 'active' | 'inactive')
        }
        options={[
          { key: 'all', label: t.aiManagement.agents.filterStatusAll },
          {
            key: 'active',
            label: t.aiManagement.agents.filterStatusActive,
          },
          {
            key: 'inactive',
            label: t.aiManagement.agents.filterStatusInactive,
          },
        ]}
      />

      {hasFilters && (
        <Button onPress={clearFilters} variant="ghost" size="large">
          {t.aiManagement.agents.actions.clearFilters}
        </Button>
      )}

      <Spacer />

      <Menu
        trigger={
          <Button variant="solid" color="primary" icon={<LucidePlusCircle />}>
            {t.aiManagement.agents.actions.create}
            <LucideChevronDown className="ml-1 size-4" />
          </Button>
        }
        items={[
          {
            label: t.aiManagement.agents.actions.createSubAgent,
            onAction: () =>
              navigate(adminRoutes.aiAgentNew, {
                query: { type: 'SubAgent' },
              }),
          },
          {
            label: t.aiManagement.agents.actions.createAgent,
            onAction: () =>
              navigate(adminRoutes.aiAgentNew, {
                query: { type: 'Agent' },
              }),
          },
          {
            label: t.aiManagement.agents.actions.createFileProcessor,
            onAction: () =>
              navigate(adminRoutes.aiAgentNew, {
                query: { type: 'FileProcessor' },
              }),
          },
        ]}
      />
    </>
  );
};
